import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useGame } from "@/context/GameContext";
import { getPaymentStatus } from "@/lib/payments";
import { Sound } from "@/lib/sound";

const MAX_TRIES = 6;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { setUnlocked, triggerConfetti } = useGame();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const onPaid = useRef(null);
  onPaid.current = (res) => { setUnlocked(res.email_hint, res); triggerConfetti(); };

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return undefined; }
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (res.payment_status === "paid") {
          onPaid.current(res);
          setStatus("paid");
          Sound.fanfare();
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") { setStatus("error"); return; }
        tries += 1;
        if (tries >= MAX_TRIES) { setStatus("pending"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 dotted-bg" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-3xl border-4 border-emerald-300 p-8 text-center card-3d">
        {status === "checking" && (
          <>
            <div className="text-6xl mb-3 animate-bounce-slow">💳</div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Checking your payment…</h1>
            <p className="text-slate-600 font-medium mt-2">Just a second while we confirm with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="text-6xl mb-3 animate-bounce-slow">🎉</div>
            <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="payment-success-title">All businesses unlocked!</h1>
            <p className="text-slate-600 font-medium mt-2">Babysitter, Craft Maker and Lawn Mower are ready. Thanks for supporting a young boss!</p>
          </>
        )}
        {status === "pending" && (
          <>
            <div className="text-6xl mb-3">⏳</div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Almost there</h1>
            <p className="text-slate-600 font-medium mt-2">Your payment is still processing. Use “Already paid? Restore” with the parent email in a minute.</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="text-6xl mb-3">😕</div>
            <h1 className="font-display text-3xl font-bold text-slate-900">We couldn't confirm that</h1>
            <p className="text-slate-600 font-medium mt-2">If you were charged, use “Already paid? Restore” with the parent email.</p>
          </>
        )}
        <Button onClick={() => navigate("/")} data-testid="payment-back-btn" className="mt-6 w-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-heading font-black rounded-2xl btn-3d border-2 border-amber-500 py-6">
          Back to my businesses →
        </Button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
